class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.addEventListener('click', this.onDrawerClick.bind(this));
    this.addEventListener('keydown', this.onDrawerKeydown.bind(this));
    this.bindSwipeToDismiss();
  }

  bindSwipeToDismiss() {
    const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
    let startY = 0;
    let currentY = 0;
    let dragging = false;

    const getPanel = () => this.querySelector('.drawer__inner');

    const onStart = (event) => {
      if (!isMobile()) return;

      dragging = true;
      startY = event.touches ? event.touches[0].clientY : event.clientY;
      currentY = startY;

      const panel = getPanel();

      if (panel) {
        panel.style.transition = 'none';
      }
    };

    const onMove = (event) => {
      if (!dragging) return;

      currentY = event.touches ? event.touches[0].clientY : event.clientY;

      const delta = Math.max(0, currentY - startY);
      const panel = getPanel();

      if (panel) {
        panel.style.transform = `translateY(${delta}px)`;
      }
    };

    const onEnd = () => {
      if (!dragging) return;

      dragging = false;

      const delta = Math.max(0, currentY - startY);
      const panel = getPanel();

      if (panel) {
        panel.style.transition = '';
        panel.style.transform = '';
      }

      if (delta > 80) {
        this.close();
      }

      startY = 0;
      currentY = 0;
    };

    this.addEventListener(
      'touchstart',
      (event) => {
        if (event.target.closest('.drawer__header')) {
          onStart(event);
        }
      },
      { passive: true }
    );

    this.addEventListener(
      'touchmove',
      (event) => {
        if (dragging) {
          onMove(event);
        }
      },
      { passive: true }
    );

    this.addEventListener('touchend', onEnd);
    this.addEventListener('touchcancel', onEnd);
  }

  onDrawerClick(event) {
    const tab = event.target.closest('[data-gx-cart-tab]');

    if (tab) {
      this.showTab(tab.dataset.gxCartTab);
      return;
    }

    const toggle = event.target.closest('[data-gx-panel-toggle]');

    if (toggle) {
      this.togglePanel(toggle);
      return;
    }

    if (event.target.closest('[data-gx-discount-apply]')) {
      this.applyDiscount();
    }
  }

  onDrawerKeydown(event) {
    if (
      event.key === 'Enter' &&
      event.target.matches('[data-gx-discount-input]')
    ) {
      event.preventDefault();
      this.applyDiscount();
    }
  }

  showTab(name) {
    const showRecent = name === 'recent';

    this.querySelectorAll('[data-gx-cart-tab]').forEach((tab) => {
      const isActive = tab.dataset.gxCartTab === name;

      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
    });

    const items = this.querySelector('cart-drawer-items');

    if (items) {
      items.hidden = showRecent;
    }

    const emptyState = this.querySelector('.drawer__inner-empty');

    if (emptyState) {
      emptyState.style.display = showRecent ? 'none' : '';
    }

    const shippingBar = this.querySelector('.gx-fsb');

    if (shippingBar) {
      shippingBar.style.display = showRecent ? 'none' : '';
    }

    const recent = this.querySelector('[data-gx-cart-recent]');

    if (!recent) return;

    recent.hidden = !showRecent;

    if (showRecent) {
      this.loadRecentlyViewed(recent);
    }
  }

  togglePanel(trigger) {
    const panel = this.querySelector(
      `#${trigger.getAttribute('data-gx-panel-toggle')}`
    );

    if (!panel) return;

    const willOpen = panel.hidden;

    this.querySelectorAll('.gx-cart-panel').forEach((element) => {
      element.hidden = true;
    });

    this.querySelectorAll('[data-gx-panel-toggle]').forEach((element) => {
      element.setAttribute('aria-expanded', 'false');
    });

    panel.hidden = !willOpen;
    trigger.setAttribute('aria-expanded', String(willOpen));

    if (willOpen) {
      panel.querySelector('input, textarea')?.focus();
    }
  }

  applyDiscount() {
    const input = this.querySelector('[data-gx-discount-input]');
    const code = input ? input.value.trim() : '';

    if (!code) {
      input?.focus();
      return;
    }

    const root = CartDrawer.rootUrl();

    window.location.href =
      `${root}discount/${encodeURIComponent(code)}` +
      `?redirect=${encodeURIComponent(`${root}checkout`)}`;
  }

  loadRecentlyViewed(panel) {
    if (panel.dataset.loaded === 'true') return;

    panel.dataset.loaded = 'true';

    let handles = [];

    try {
      handles =
        JSON.parse(window.localStorage.getItem('gx-recently-viewed')) || [];
    } catch (error) {
      handles = [];
    }

    handles = handles.slice(0, 4);

    if (!handles.length) {
      CartDrawer.renderRecentEmpty(panel);
      return;
    }

    const root = CartDrawer.rootUrl(panel.dataset.rootUrl);

    Promise.all(
      handles.map((handle) =>
        fetch(`${root}products/${encodeURIComponent(handle)}.js`)
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      )
    )
      .then((products) => {
        const found = products.filter(Boolean);

        if (!found.length) {
          CartDrawer.renderRecentEmpty(panel);
          return;
        }

        const fragment = document.createDocumentFragment();

        found.forEach((product) => {
          fragment.appendChild(
            CartDrawer.buildRecentCard(product, root)
          );
        });

        panel.appendChild(fragment);
      })
      .catch(() => CartDrawer.renderRecentEmpty(panel));
  }

  static renderRecentEmpty(panel) {
    const message = document.createElement('p');

    message.className = 'gx-cart-recent__empty';
    message.textContent =
      panel.dataset.emptyText ||
      'Products you view will appear here.';

    panel.appendChild(message);
  }

  static rootUrl(value) {
    const root =
      value ||
      window.Shopify?.routes?.root ||
      '/';

    return root.replace(/\/?$/, '/');
  }

  static formatMoney(cents) {
    const currency =
      window.Shopify?.currency?.active ||
      'USD';

    try {
      return new Intl.NumberFormat(
        document.documentElement.lang || undefined,
        {
          style: 'currency',
          currency
        }
      ).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  }

  static buildRecentCard(product, root) {
    const link = document.createElement('a');

    link.href = `${root}products/${product.handle}`;
    link.className = 'gx-cart-recent-card';

    const media = document.createElement('div');
    media.className = 'gx-cart-recent-card__media';

    if (product.featured_image) {
      const image = document.createElement('img');

      image.src = product.featured_image.includes('?')
        ? product.featured_image
        : `${product.featured_image}?width=400`;

      image.loading = 'lazy';
      image.alt = '';

      media.appendChild(image);
    }

    const title = document.createElement('p');

    title.className = 'gx-cart-recent-card__title';
    title.textContent = product.title;

    const price = document.createElement('p');

    price.className = 'gx-cart-recent-card__price';
    price.textContent = CartDrawer.formatMoney(product.price);

    link.append(media, title, price);

    return link;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;

    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const cartDrawerNote = this.querySelector(
      '[id^="Details-"] summary'
    );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn =
          this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        trapFocus(
          containerToTrapFocusOn,
          focusElement
        );
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    this.querySelector(
      'cart-drawer-items'
    )?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');

    removeTrapFocus(this.activeElement);

    document.body.classList.remove(
      'overflow-hidden'
    );
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute(
      'aria-expanded',
      'false'
    );

    if (
      cartDrawerNote.nextElementSibling.getAttribute(
        'id'
      )
    ) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener(
      'click',
      (event) => {
        event.currentTarget.setAttribute(
          'aria-expanded',
          !event.currentTarget
            .closest('details')
            .hasAttribute('open')
        );
      }
    );

    cartDrawerNote.parentElement.addEventListener(
      'keyup',
      onKeyUpEscape
    );
  }

  renderContents(parsedState) {
    const drawerInner =
      this.querySelector('.drawer__inner');

    if (
      drawerInner &&
      drawerInner.classList.contains('is-empty')
    ) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      sectionElement.innerHTML =
        this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );
    });

    setTimeout(() => {
      const overlay =
        this.querySelector('#CartDrawer-Overlay');

      if (overlay) {
        overlay.addEventListener(
          'click',
          this.close.bind(this)
        );
      }

      this.open();
    });
  }

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    const documentObject =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const element =
      documentObject.querySelector(selector);

    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    CartDrawer
  );
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define(
    'cart-drawer-items',
    CartDrawerItems
  );
}